import { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from '@dukanti/shared';
import type { StockWorklistFilter, StockWorklistScope } from '@dukanti/shared/admin';
import { AlertTriangle, ChevronDown, Search } from 'lucide-react';

export interface StockFilterBarProps {
  readonly scope: StockWorklistScope;
  readonly onScopeChange: (scope: StockWorklistScope) => void;
  readonly filter: StockWorklistFilter;
  readonly onFilterChange: (filter: StockWorklistFilter) => void;
  readonly searchQuery: string;
  readonly onSearchChange: (query: string) => void;
  readonly onClear: () => void;
  readonly matchingCount: number | null;
  readonly loadedCount: number;
  readonly resetKey: string;
}

export function StockFilterBar({
  scope,
  onScopeChange,
  filter,
  onFilterChange,
  searchQuery,
  onSearchChange,
  onClear,
  matchingCount,
  loadedCount,
  resetKey,
}: StockFilterBarProps): React.JSX.Element {
  const { t, dir, lang } = useTranslation();
  const id = useId();
  const [draft, setDraft] = useState(searchQuery);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const composing = useRef(false);
  const cancel = (): void => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
  };
  useEffect(() => {
    cancel();
    composing.current = false;
    setDraft(searchQuery);
    return cancel;
  }, [searchQuery, scope, filter, resetKey]);
  const schedule = (value: string): void => {
    cancel();
    if (composing.current || [...value.trim()].length > 100) return;
    timer.current = setTimeout(() => {
      if (value.trim() !== searchQuery) onSearchChange(value.trim());
    }, 300);
  };
  const invalid = [...draft.trim()].length > 100;
  const fmt = (value: number): string => new Intl.NumberFormat(lang).format(value);
  const choices = [
    { value: 'all', key: 'all', label: t('admin.stock.filter.all') },
    { value: 'out_of_stock', key: 'out-of-stock', label: t('admin.stock.filter.outOfStock') },
    { value: 'low_stock', key: 'low-stock', label: t('admin.stock.filter.lowStock') },
  ] as const;
  return (
    <div
      dir={dir}
      data-testid="stock-filter-bar"
      className="border-border bg-card shadow-admin-card space-y-3 rounded-xl border p-3 sm:p-4"
    >
      <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_auto]">
        <div>
          <label htmlFor={`${id}-search`} className="sr-only">
            {t('admin.stock.worklist.search')}
          </label>
          <div className="border-input bg-background focus-within:ring-ring flex min-w-0 items-center rounded-md border focus-within:ring-2">
            {/* Keep decoration outside the bidi input's native clear-control area. */}
            <Search
              aria-hidden="true"
              size={16}
              className="text-muted-foreground pointer-events-none ms-3 shrink-0"
            />
            <input
              id={`${id}-search`}
              data-testid="stock-search-input"
              type="search"
              dir="auto"
              value={draft}
              placeholder={t('admin.stock.worklist.search')}
              aria-invalid={invalid}
              aria-describedby={invalid ? `${id}-error` : undefined}
              onChange={(event) => {
                setDraft(event.target.value);
                schedule(event.target.value);
              }}
              onKeyDown={(event) => {
                if (
                  event.key !== 'Escape' ||
                  composing.current ||
                  event.nativeEvent.isComposing ||
                  !draft
                )
                  return;
                event.preventDefault();
                cancel();
                setDraft('');
                if (searchQuery) onSearchChange('');
              }}
              onCompositionStart={() => {
                composing.current = true;
                cancel();
              }}
              onCompositionEnd={(event) => {
                composing.current = false;
                setDraft(event.currentTarget.value);
                schedule(event.currentTarget.value);
              }}
              className="text-foreground placeholder:text-muted-foreground min-h-11 w-full min-w-0 rounded-md border-0 bg-transparent px-3 text-sm focus-visible:outline-none"
            />
          </div>
          {invalid ? (
            <p
              id={`${id}-error`}
              role="alert"
              className="text-foreground mt-1 flex items-start gap-2 text-sm"
            >
              <AlertTriangle aria-hidden="true" size={16} className="mt-0.5 shrink-0" />
              <span>{t('admin.stock.worklist.searchTooLong')}</span>
            </p>
          ) : null}
        </div>
        <div className="relative">
          <label htmlFor={`${id}-scope`} className="sr-only">
            {t('admin.stock.worklist.scopeLabel')}
          </label>
          <select
            id={`${id}-scope`}
            data-testid="stock-scope"
            value={scope}
            onChange={(event) => {
              cancel();
              composing.current = false;
              setDraft(searchQuery);
              onScopeChange(event.target.value === 'all' ? 'all' : 'active');
            }}
            className="border-input bg-background text-foreground focus-visible:ring-ring min-h-11 w-full appearance-none rounded-md border bg-none pe-10 ps-3 text-start text-sm [-moz-appearance:none] [-webkit-appearance:none] focus-visible:outline-none focus-visible:ring-2"
          >
            <option value="active">{t('admin.stock.worklist.scopeActive')}</option>
            <option value="all">{t('admin.stock.worklist.scopeAll')}</option>
          </select>
          <ChevronDown
            aria-hidden="true"
            size={16}
            className="pointer-events-none absolute end-3 top-1/2 -translate-y-1/2"
          />
        </div>
      </div>
      <fieldset className="flex flex-wrap gap-2">
        <legend className="sr-only">{t('admin.stock.columns.status')}</legend>
        {choices.map(({ value, key, label }) => (
          <label key={value} className="relative cursor-pointer">
            <input
              className="peer sr-only"
              type="radio"
              name={`${id}-status`}
              value={value}
              checked={filter === value}
              data-testid={`stock-filter-${key}`}
              onChange={() => {
                cancel();
                composing.current = false;
                setDraft(searchQuery);
                onFilterChange(value);
              }}
            />
            <span className="border-input text-muted-foreground peer-checked:bg-admin-lime peer-checked:text-primary-foreground peer-focus-visible:ring-ring inline-flex min-h-11 items-center rounded-md border px-3 text-sm peer-focus-visible:ring-2">
              {label}
            </span>
          </label>
        ))}
        {draft || filter !== 'all' ? (
          <button
            type="button"
            onClick={() => {
              cancel();
              composing.current = false;
              setDraft('');
              onClear();
            }}
            className="text-muted-foreground focus-visible:ring-ring min-h-11 rounded-md px-2 text-xs underline underline-offset-4 focus-visible:ring-2"
          >
            {t('admin.stock.worklist.clear')}
          </button>
        ) : null}
      </fieldset>
      {matchingCount !== null ? (
        <p data-testid="stock-filter-count" className="text-muted-foreground text-xs tabular-nums">
          {t('admin.stock.worklist.results', {
            total: fmt(matchingCount),
            loaded: fmt(loadedCount),
          })}
        </p>
      ) : null}
    </div>
  );
}
